import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '@/components/ui/PageHeader/PageHeader';
import { PageContainer } from '@/components/ui/PageContainer/PageContainer';
import { Table, type TableColumn } from '@/components/ui/Table';
import { Button, Badge, AIIcon, Input, Select, Autocomplete, ColumnChooser, ConfirmDialog, TableActionButton, TableActions } from '@/components/ui';
import { SimpleTooltip } from '@/components/ui/Tooltip';
import { VenuesService, type Venue, type VenueFilters } from '@/services/VenuesService';
import { VenueImportModal } from './VenueImportModal';
import { VenueFormModal } from './VenueFormModal';
import { GooglePlacesImportModal } from './GooglePlacesImportModal';
import { VenueDetailsModal } from '@/routes/tools/research-proposal/components/WizardSteps/Step1Research/VenueDetailsModal';
import type { VenueDetails } from '@/services/ResearchService';
import { useNotificationStore } from '@/stores/notificationStore';
import { usePermissions } from '@/hooks/usePermissions';
import { PERMISSIONS } from '@/constants/permissions';
import { useColumnVisibility } from '@/hooks/useColumnVisibility';
import { TIMING } from '@/constants/timing';
import { Plus, Upload, Loader2, AlertTriangle, RotateCcw, Filter, ChevronDown, ChevronUp, ExternalLink, Download, Plane, Sparkles, FilePen, Trash2, MapPin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { StarRating } from '@/components/ui/StarRating';

export function VenuesManagement() {
  const navigate = useNavigate();
  const { hasAnyPermission } = usePermissions();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [filters, setFilters] = useState<VenueFilters>({});
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [googlePlacesModalOpen, setGooglePlacesModalOpen] = useState(false);
  const [editingVenue, setEditingVenue] = useState<Venue | null>(null);
  const [enrichingVenues, setEnrichingVenues] = useState<Set<string>>(new Set());
  
  // Venue details modal state
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedVenue, setSelectedVenue] = useState<VenueDetails | null>(null);
  
  // Permission checks
  const canCreate = hasAnyPermission([PERMISSIONS.CREATE_VENUES, PERMISSIONS.MANAGE_VENUES]);
  const canUpdate = hasAnyPermission([PERMISSIONS.UPDATE_VENUES, PERMISSIONS.MANAGE_VENUES]);
  const canDelete = hasAnyPermission([PERMISSIONS.DELETE_VENUES, PERMISSIONS.MANAGE_VENUES]);
  const canRead = hasAnyPermission([PERMISSIONS.READ_VENUES, PERMISSIONS.MANAGE_VENUES]);
  
  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [selectAllFiltered, setSelectAllFiltered] = useState(false);
  
  // Export state
  const [exportScope, setExportScope] = useState<'page' | 'filtered' | 'all'>('filtered');
  const [showExportMenu, setShowExportMenu] = useState(false);
  
  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    description: string;
    action: (() => void) | null;
    variant?: 'primary' | 'destructive' | 'danger';
  }>({
    isOpen: false,
    title: '',
    description: '',
    action: null,
    variant: 'primary'
  });
  
  const queryClient = useQueryClient();
  const { push: addNotification } = useNotificationStore();
  const venuesService = new VenuesService();

  // Column visibility management
  const { columns: columnConfig, visibleColumns, updateColumns, resetColumns } = useColumnVisibility({
    storageKey: 'venues_table_columns_v2',
    defaultColumns: [
      { key: 'selection', label: 'Select', visible: true, required: true },
      { key: 'property_name', label: 'Property Name', visible: true, required: true },
      { key: 'city', label: 'City', visible: false },
      { key: 'country', label: 'Country', visible: false },
      { key: 'website', label: 'Website', visible: false },
      { key: 'number_of_rooms', label: 'Rooms', visible: true },
      { key: 'meeting_spaces', label: 'Meeting Spaces', visible: false },
      { key: 'maximum_capacity', label: 'Max Capacity', visible: false },
      { key: 'star_rating', label: 'Stars', visible: true },
      { key: 'nearest_airport', label: 'Nearest Airport', visible: true },
      { key: 'airport_distance', label: 'Distance to Airport (km)', visible: false },
      { key: 'travel_time', label: 'Travel Time (min)', visible: false },
      { key: 'nearby_airports', label: 'Nearby Airports', visible: false },
      { key: 'tripadvisor_rating', label: 'TripAdvisor', visible: false },
      { key: 'spa_facilities', label: 'Spa', visible: false },
      { key: 'beach_access', label: 'Beach', visible: false },
      { key: 'all_inclusive', label: 'All-Inclusive', visible: false },
      { key: 'enrichment_status', label: 'AI Research', visible: true },
      { key: 'actions', label: 'Actions', visible: true, required: true }
    ]
  });

  const { data, isLoading } = useQuery({
    queryKey: ['venues', page, pageSize, filters],
    queryFn: () => venuesService.list(page, pageSize, filters),
    staleTime: 30000 // 30 seconds
  });

  // Fetch ungeolocated count
  const { data: ungeolocatedCount } = useQuery({
    queryKey: ['venues', 'ungeolocated-count'],
    queryFn: () => venuesService.getUngeolocatedCount(),
    staleTime: 60000 // 1 minute
  });

  // Poll for updates when venues have active jobs (queued or processing)
  const hasProcessingVenues = data?.items?.some(
    v => v.enrichment_job_status === 'queued' || v.enrichment_job_status === 'processing' || enrichingVenues.has(v.id)
  ) || false;

  // Clean up enrichingVenues state when venues no longer have active jobs
  useEffect(() => {
    if (!data?.items) return;
    
    setEnrichingVenues(prev => {
      const next = new Set(prev);
      let changed = false;

      // Remove venues that no longer have active jobs from the enriching set
      prev.forEach(venueId => {
        const venue = data.items.find(v => v.id === venueId);
        if (venue && !venue.enrichment_job_status) {
          next.delete(venueId);
          changed = true;
        }
      });

      return changed ? next : prev;
    });
  }, [data?.items]);

  // Close export menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (showExportMenu && !target.closest('.export-dropdown')) {
        setShowExportMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showExportMenu]);

  useEffect(() => {
    if (!hasProcessingVenues) return;
    
    const interval = setInterval(() => {
      queryClient.invalidateQueries({ queryKey: ['venues'] });
    }, TIMING.POLLING.VENUE_ENRICHMENT);

    return () => clearInterval(interval);
  }, [hasProcessingVenues, queryClient]);

  const enrichMutation = useMutation({
    mutationFn: (venueId: string) => {
      // Add to enriching set immediately
      setEnrichingVenues(prev => new Set(prev).add(venueId));
      return venuesService.triggerEnrichment(venueId);
    },
    onSuccess: (_, venueId) => {
      addNotification({
        type: 'success',
        title: 'Research Started',
        message: 'AI Research Agent is now working on this venue'
      });
      queryClient.invalidateQueries({ queryKey: ['venues'] });
    },
    onError: (error, venueId) => {
      // Remove from enriching set on error
      setEnrichingVenues(prev => {
        const next = new Set(prev);
        next.delete(venueId);
        return next;
      });
      
      const errorMessage = error instanceof Error ? error.message : 'Failed to trigger research';
      addNotification({
        type: 'error',
        title: 'Research Failed',
        message: errorMessage === 'Enrichment already in progress for this venue' 
          ? 'Research is already in progress for this venue' 
          : errorMessage
      });
    },
    onSettled: () => {
      // Refresh after a short delay to get updated status
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: ['venues'] });
      }, 1000);
    }
  });

  const resetMutation = useMutation({
    mutationFn: (venueId: string) => venuesService.resetEnrichment(venueId),
    onSuccess: () => {
      addNotification({
        type: 'success',
        title: 'Research Reset',
        message: 'Research status has been reset successfully'
      });
      queryClient.invalidateQueries({ queryKey: ['venues'] });
    },
    onError: (error) => {
      addNotification({
        type: 'error',
        title: 'Reset Failed',
        message: error instanceof Error ? error.message : 'Failed to reset research'
      });
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (venueId: string) => venuesService.delete(venueId),
    onSuccess: () => {
      addNotification({
        type: 'success',
        title: 'Venue Deleted',
        message: 'Venue has been successfully deleted'
      });
      queryClient.invalidateQueries({ queryKey: ['venues'] });
    }
  });

  const bulkActionMutation = useMutation({
    mutationFn: ({ action, selection }: { action: 'delete' | 'enrich' | 'refresh_airports'; selection: { allFiltered: boolean; ids?: string[] } }) =>
      venuesService.bulkAction(action, selection, filters),
    onSuccess: (result, variables) => {
      const actionLabels = {
        delete: 'Deleted',
        enrich: 'Research Queued',
        refresh_airports: 'Airports Refreshed'
      };
      addNotification({
        type: 'success',
        title: actionLabels[variables.action],
        message: result.message
      });
      setSelectedIds(new Set());
      setSelectAllFiltered(false);
      queryClient.invalidateQueries({ queryKey: ['venues'] });
    },
    onError: (error, variables) => {
      addNotification({
        type: 'error',
        title: 'Bulk Action Failed',
        message: error instanceof Error ? error.message : `Failed to ${variables.action} venues`
      });
    }
  });

  const handleSort = useCallback((key: string, direction: 'asc' | 'desc') => {
    setFilters(prev => ({ ...prev, sort_by: key, sort_dir: direction }));
    setPage(1);
  }, []);

  const fetchCities = useCallback(async (search: string) => {
    return await venuesService.getFilterCities(search);
  }, []);

  const fetchCountries = useCallback(async (search: string) => {
    return await venuesService.getFilterCountries(search);
  }, []);

  const updateFilter = useCallback((key: keyof VenueFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value || undefined }));
    setPage(1);
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({ search: filters.search }); // Keep search
    setPage(1);
  }, [filters.search]);

  const hasActiveFilters = Object.keys(filters).some(
    key => key !== 'search' && key !== 'sort_by' && key !== 'sort_dir' && filters[key as keyof VenueFilters]
  );

  // Bulk selection handlers
  const handleSelectAll = useCallback(() => {
    if (selectAllFiltered) {
      setSelectAllFiltered(false);
      setSelectedIds(new Set());
    } else {
      const currentPageIds = data?.items.map(v => v.id) || [];
      const allSelected = currentPageIds.every(id => selectedIds.has(id));
      
      if (allSelected) {
        // Deselect all on page
        setSelectedIds(new Set());
      } else {
        // Select all on page
        const newSelected = new Set(selectedIds);
        currentPageIds.forEach(id => newSelected.add(id));
        setSelectedIds(newSelected);
      }
    }
  }, [data?.items, selectedIds, selectAllFiltered]);

  const handleSelectRow = useCallback((venueId: string) => {
    if (selectAllFiltered) return; // Don't allow individual selection when all filtered is on
    
    const newSelected = new Set(selectedIds);
    if (newSelected.has(venueId)) {
      newSelected.delete(venueId);
    } else {
      newSelected.add(venueId);
    }
    setSelectedIds(newSelected);
  }, [selectedIds, selectAllFiltered]);

  const handleSelectAllFiltered = useCallback(() => {
    setSelectAllFiltered(true);
    setSelectedIds(new Set());
  }, []);

  const handleBulkAction = useCallback((action: 'delete' | 'enrich' | 'refresh_airports') => {
    const selectionCount = selectAllFiltered 
      ? (data?.pagination?.total || 0)
      : selectedIds.size;

    if (selectionCount === 0) {
      addNotification({
        type: 'warning',
        title: 'No Selection',
        message: 'Please select venues to perform this action'
      });
      return;
    }

    const actionConfig = {
      delete: {
        title: 'Delete Venues',
        message: 'delete',
        variant: 'danger' as const
      },
      enrich: {
        title: 'Run AI Research',
        message: 'queue AI research for',
        variant: 'primary' as const
      },
      refresh_airports: {
        title: 'Refresh Airports',
        message: 'refresh nearest airports for',
        variant: 'primary' as const
      }
    };

    const config = actionConfig[action];
    const scope = selectAllFiltered ? 'all matching filters' : 'selected';
    
    setConfirmDialog({
      isOpen: true,
      title: config.title,
      description: `Are you sure you want to ${config.message} ${selectionCount} ${scope} venue${selectionCount === 1 ? '' : 's'}?`,
      variant: config.variant,
      action: () => {
        bulkActionMutation.mutate({
          action,
          selection: {
            allFiltered: selectAllFiltered,
            ids: selectAllFiltered ? undefined : Array.from(selectedIds)
          }
        });
      }
    });
  }, [selectAllFiltered, selectedIds, data?.pagination?.total, bulkActionMutation, addNotification]);

  const selectionCount = selectAllFiltered ? (data?.pagination?.total || 0) : selectedIds.size;
  const hasSelection = selectionCount > 0;

  const handleExport = useCallback(async (scope: 'page' | 'filtered' | 'all') => {
    try {
      // Build URL with current filters and scope
      const params = new URLSearchParams({
        scope,
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, v]) => v !== undefined).map(([k, v]) => [k, String(v)])
        )
      });

      if (scope === 'page') {
        params.set('page', String(page));
        params.set('pageSize', String(pageSize));
      }

      setShowExportMenu(false);
      
      addNotification({
        type: 'info',
        title: 'Export Started',
        message: 'Preparing your CSV export...'
      });

      // Use apiFetch to include X-Organization-Id header
      const response = await venuesService.exportCSV(params);
      
      // Create blob and trigger download
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `venues-export-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      addNotification({
        type: 'success',
        title: 'Export Complete',
        message: 'Your CSV export has been downloaded'
      });
    } catch (error) {
      addNotification({
        type: 'error',
        title: 'Export Failed',
        message: error instanceof Error ? error.message : 'Failed to export venues'
      });
    }
  }, [filters, page, pageSize, venuesService, addNotification]);

  // Define all possible columns
  const allColumns = useMemo<TableColumn<Venue>[]>(() => {
    const selectionColumn: TableColumn<Venue> = {
      key: 'selection',
      title: '' as any, // Will be set below
      width: '50px',
      render: (_value: any, record: Venue) => (
        <input
          type="checkbox"
          checked={selectAllFiltered || selectedIds.has(record.id)}
          onChange={() => handleSelectRow(record.id)}
          disabled={selectAllFiltered}
          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label={`Select ${record.property_name}`}
        />
      )
    };
    
    // Set the header checkbox separately to avoid issues
    selectionColumn.title = (
      <input
        type="checkbox"
        checked={selectAllFiltered || ((data?.items?.length ?? 0) > 0 && data?.items?.every(v => selectedIds.has(v.id)) === true)}
        onChange={handleSelectAll}
        className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 cursor-pointer"
        aria-label="Select all venues"
      />
    ) as any;
    
    return [
    selectionColumn,
    {
      key: 'property_name',
      title: 'Property Name',
      sortable: true,
      render: (value: string, record: Venue) => (
        <div>
          <button
            onClick={() => {
              // Convert Venue to VenueDetails format
              setSelectedVenue(record as unknown as VenueDetails);
              setDetailsModalOpen(true);
            }}
            className="font-medium text-secondary-600 hover:text-secondary-700 hover:underline text-left"
          >
            {value}
          </button>
          {/* Show city/country subtitle only if those columns aren't visible separately */}
          {!visibleColumns.find(c => c.key === 'city') && !visibleColumns.find(c => c.key === 'country') && record.city && (
            <div className="text-sm text-gray-500">
              {record.city}{record.country ? `, ${record.country}` : ''}
            </div>
          )}
        </div>
      )
    },
    {
      key: 'city',
      title: 'City',
      render: (value?: string) => value || '-'
    },
    {
      key: 'country',
      title: 'Country',
      render: (value?: string) => value || '-'
    },
    {
      key: 'website',
      title: 'Website',
      render: (value?: string) => 
        value ? (
          <a 
            href={value} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-secondary-600 hover:text-secondary-700 inline-flex items-center gap-1 text-sm"
          >
            <ExternalLink className="w-3 h-3" />
            Visit
          </a>
        ) : '-'
    },
    {
      key: 'number_of_rooms',
      title: 'Rooms',
      sortable: true,
      render: (value?: number) => value?.toLocaleString() || '-'
    },
    {
      key: 'meeting_spaces',
      title: 'Meeting Spaces',
      render: (value?: number) => value || '-'
    },
    {
      key: 'maximum_capacity',
      title: 'Max Capacity',
      sortable: true,
      render: (value?: number) => value?.toLocaleString() || '-'
    },
    {
      key: 'star_rating',
      title: 'Stars',
      sortable: true,
      render: (value?: number) => value ? <StarRating rating={value} size="sm" showEmptyStars={true} /> : '-'
    },
    {
      key: 'nearest_airport',
      title: 'Nearest Airport',
      render: (_value: any, record: Venue) => {
        if (!record.nearest_airport_iata) {
          return (
            <div 
              className="inline-flex items-center gap-1 text-gray-400"
              title="No airports assigned"
            >
              <Plane className="w-3 h-3" />
              <span className="text-xs">None</span>
            </div>
          );
        }
        
        const showDistance = !visibleColumns.find(c => c.key === 'airport_distance');
        const distance = record.nearest_airport_distance_km 
          ? Number(record.nearest_airport_distance_km)
          : null;
        
        const tooltipContent = distance
          ? `${record.nearest_airport_name || record.nearest_airport_iata} - ${distance.toFixed(1)}km away`
          : record.nearest_airport_name || record.nearest_airport_iata;
        
        return (
          <SimpleTooltip
            content={tooltipContent}
            side="top"
          >
            <div className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs">
              <Plane className="w-3 h-3" />
              <span className="font-medium">{record.nearest_airport_iata}</span>
              {showDistance && distance && (
                <span className="text-blue-500">{distance.toFixed(0)}km</span>
              )}
            </div>
          </SimpleTooltip>
        );
      }
    },
    {
      key: 'airport_distance',
      title: 'Distance to Airport (km)',
      sortable: true,
      render: (_value: any, record: Venue) =>
        record.nearest_airport_distance_km 
          ? Number(record.nearest_airport_distance_km).toFixed(1)
          : '-'
    },
    {
      key: 'travel_time',
      title: 'Travel Time (min)',
      render: (value?: number) => value || '-'
    },
    {
      key: 'nearby_airports',
      title: 'Nearby Airports',
      render: (_value: any, record: Venue) => {
        const airports = record.nearby_airports || [];
        
        if (airports.length === 0) {
          return (
            <div 
              className="inline-flex items-center gap-1 text-gray-400"
              title="No airports assigned"
            >
              <Plane className="w-3 h-3" />
              <span className="text-xs">None</span>
            </div>
          );
        }
        
        return (
          <div className="flex flex-wrap gap-1">
            {airports.slice(0, 2).map((airport) => (
              <SimpleTooltip
                key={airport.iata}
                content={`${airport.name} - ${airport.distance_km.toFixed(1)}km away (rank ${airport.rank})`}
                side="top"
              >
                <div className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs">
                  <Plane className="w-3 h-3" />
                  <span className="font-medium">{airport.iata}</span>
                  <span className="text-blue-500">{airport.distance_km.toFixed(0)}km</span>
                </div>
              </SimpleTooltip>
            ))}
            {airports.length > 2 && (
              <div className="relative">
                <SimpleTooltip
                  content={
                    <div className="space-y-1">
                      <div className="font-medium text-xs">Additional Airports:</div>
                      {airports.slice(2).map((airport) => (
                        <div key={airport.iata} className="flex items-center justify-between gap-2 text-xs">
                          <div className="flex items-center gap-1">
                            <Plane className="w-3 h-3" />
                            <span className="font-medium">{airport.iata}</span>
                            <span className="text-gray-500">{airport.name}</span>
                          </div>
                          <span className="text-gray-600">{airport.distance_km.toFixed(1)}km</span>
                        </div>
                      ))}
                    </div>
                  }
                  side="top"
                >
                  <div className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs cursor-help">
                    <span>+{airports.length - 2} more</span>
                  </div>
                </SimpleTooltip>
              </div>
            )}
          </div>
        );
      }
    },
    {
      key: 'tripadvisor_rating',
      title: 'TripAdvisor',
      render: (_value: any, record: Venue) => {
        const rating = record.tripadvisor_rating ? Number(record.tripadvisor_rating) : null;
        
        if (!rating) return '-';
        
        // Parse reviews if available
        const reviews = record.tripadvisor_reviews_json 
          ? (typeof record.tripadvisor_reviews_json === 'string' 
              ? JSON.parse(record.tripadvisor_reviews_json) 
              : record.tripadvisor_reviews_json)
          : null;
        
        // Build tooltip content with recent reviews
        const tooltipContent = reviews && reviews.length > 0 ? (
          <div className="max-w-md space-y-3">
            <div className="font-semibold text-sm border-b border-gray-200 pb-2">
              Recent Reviews ({reviews.length} available)
            </div>
            {reviews.slice(0, 3).map((review: any, idx: number) => (
              <div key={idx} className="space-y-1 text-xs">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-gray-900">{review.title}</span>
                  <StarRating rating={review.rating} size="xs" showEmptyStars={false} />
                </div>
                {review.text && (
                  <p className="text-gray-600 line-clamp-2">{review.text}</p>
                )}
                <div className="flex items-center gap-2 text-gray-500">
                  <span>{review.author}</span>
                  <span>•</span>
                  <span>{new Date(review.date).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
            {reviews.length > 3 && (
              <div className="text-xs text-gray-500 italic pt-2 border-t border-gray-200">
                +{reviews.length - 3} more reviews available
              </div>
            )}
          </div>
        ) : (
          <div className="text-xs">
            <div className="font-medium">TripAdvisor Rating: {rating.toFixed(1)}/5.0</div>
            {record.tripadvisor_review_count && (
              <div className="text-gray-400 mt-1">Based on {record.tripadvisor_review_count} reviews</div>
            )}
            {!reviews && <div className="text-gray-400 mt-1">No review details available</div>}
          </div>
        );
        
        return (
          <SimpleTooltip content={tooltipContent} side="top">
            <div className="inline-flex items-center gap-1 cursor-help">
              <StarRating rating={rating} size="sm" showEmptyStars={false} />
              <span className="text-sm text-gray-600">{rating.toFixed(1)}</span>
              {record.tripadvisor_review_count && (
                <span className="text-xs text-gray-500">({record.tripadvisor_review_count})</span>
              )}
            </div>
          </SimpleTooltip>
        );
      }
    },
    {
      key: 'spa_facilities',
      title: 'Spa',
      render: (value?: boolean) => value ? '✓' : '-'
    },
    {
      key: 'beach_access',
      title: 'Beach',
      render: (value?: string) => {
        if (value === 'beachfront') return '🏖️ Beachfront';
        if (value === 'beach_access') return '🏖️ Access';
        return '-';
      }
    },
    {
      key: 'all_inclusive',
      title: 'All-Inclusive',
      render: (_value: any, record: Venue) =>
        record.all_inclusive_available ? '✓' : '-'
    },
    {
      key: 'enrichment_status',
      title: 'AI Research',
      render: (value: string, record: Venue) => {
        const enrichmentDate = record.enrichment_completed_at 
          ? new Date(record.enrichment_completed_at).toLocaleString()
          : null;
        
        // Determine job status from joined job data
        const jobStatus = record.enrichment_job_status;
        const isQueued = jobStatus === 'queued' || enrichingVenues.has(record.id);
        const isProcessing = jobStatus === 'processing';
        const hasActiveJob = isQueued || isProcessing;
        
        // Calculate actual processing duration (only if job has started)
        const processingDuration = record.enrichment_job_started_at 
          ? Date.now() - new Date(record.enrichment_job_started_at).getTime()
          : 0;
        
        // A job is only "stuck" if it's actually processing and has been for > 5 minutes
        const isStuck = isProcessing && processingDuration > TIMING.TIMEOUTS.JOB_STUCK;
        
        return (
          <div className="flex items-center gap-2">
            {hasActiveJob ? (
              <>
                <div className="flex items-center gap-2">
                  {isStuck && <AlertTriangle className="w-4 h-4 text-orange-500" />}
                  <Loader2 className="w-4 h-4 animate-spin text-primary-600" />
                  <Badge variant={isStuck ? 'error' : isProcessing ? 'warning' : 'default'}>
                    {isStuck ? 'Stuck' : isProcessing ? 'Processing' : 'Queued'}
                  </Badge>
                </div>
                {isStuck && canUpdate && (
                  <SimpleTooltip content="Reset Research (Cancel & Revert)" side="top">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => resetMutation.mutate(record.id)}
                      disabled={resetMutation.isPending}
                    >
                      <RotateCcw className="w-4 h-4 text-orange-500" />
                    </Button>
                  </SimpleTooltip>
                )}
              </>
            ) : (
              <>
                {canUpdate && (
                  <SimpleTooltip content="Run Venue Research" side="top">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => enrichMutation.mutate(record.id)}
                      disabled={enrichMutation.isPending}
                      className="hover:bg-accent-50"
                    >
                      <AIIcon className="w-4 h-4" />
                    </Button>
                  </SimpleTooltip>
                )}
                
                <SimpleTooltip
                  content={
                    enrichmentDate 
                      ? `Last researched: ${enrichmentDate}${record.enrichment_source ? ` (${record.enrichment_source})` : ''}`
                      : 'Not yet researched'
                  }
                  side="top"
                >
                  <Badge 
                    variant={
                      value === 'enriched' ? 'success' : 
                      value === 'failed' ? 'error' : 'default'
                    }
                  >
                    {value === 'enriched' ? 'researched' : value}
                  </Badge>
                </SimpleTooltip>
              </>
            )}
          </div>
        );
      }
    },
    {
      key: 'actions',
      title: 'Actions',
      render: (_: any, record: Venue) => (
        <TableActions>
          {canUpdate && (
            <TableActionButton
              icon={FilePen}
              onClick={() => {
                setEditingVenue(record);
                setFormModalOpen(true);
              }}
            >
              Edit
            </TableActionButton>
          )}
          {canDelete && (
            <TableActionButton
              variant="destructive"
              icon={Trash2}
              onClick={() => {
                setConfirmDialog({
                  isOpen: true,
                  title: 'Delete Venue',
                  description: `Are you sure you want to delete "${record.property_name}"? This action cannot be undone.`,
                  variant: 'danger',
                  action: () => deleteMutation.mutate(record.id)
                });
              }}
            >
              Delete
            </TableActionButton>
          )}
        </TableActions>
      )
    }
  ];
  }, [visibleColumns, selectAllFiltered, selectedIds, data?.items, handleSelectAll, handleSelectRow]);

  // Filter columns based on visibility
  const displayColumns = useMemo(() => {
    const visibleKeys = new Set(visibleColumns.map(c => c.key));
    return allColumns.filter(col => visibleKeys.has(col.key));
  }, [allColumns, visibleColumns]);

  return (
    <PageContainer>
      <PageHeader
        title="Venues Database"
        description="Manage properties and venues for event planning"
      >
        {canCreate && (
          <Button
            size="sm"
            onClick={() => {
              setEditingVenue(null);
              setFormModalOpen(true);
            }}
            leftIcon={<Plus className="w-4 h-4" />}
          >
            Add Venue
          </Button>
        )}
      </PageHeader>

      {/* Action Bar */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <ColumnChooser
          columns={columnConfig}
          onChange={updateColumns}
          onReset={resetColumns}
        />
        
        {/* Export Dropdown */}
        {canRead && (
          <div className="relative export-dropdown">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowExportMenu(!showExportMenu)}
              leftIcon={<Download className="w-4 h-4" />}
              rightIcon={<ChevronDown className="w-3 h-3" />}
            >
              Export CSV
            </Button>
            
            {showExportMenu && (
              <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                <div className="p-2">
                  <button
                    onClick={() => handleExport('page')}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 rounded-md transition-colors"
                  >
                    <div className="font-medium text-gray-900">This Page</div>
                    <div className="text-xs text-gray-500">Export {data?.items.length || 0} visible venues</div>
                  </button>
                  <button
                    onClick={() => handleExport('filtered')}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 rounded-md transition-colors"
                  >
                    <div className="font-medium text-gray-900">All Filtered</div>
                    <div className="text-xs text-gray-500">Export {data?.pagination?.total || 0} matching filters</div>
                  </button>
                  <button
                    onClick={() => handleExport('all')}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 rounded-md transition-colors"
                  >
                    <div className="font-medium text-gray-900">All Venues</div>
                    <div className="text-xs text-gray-500">Export entire database</div>
                  </button>
                </div>
                <div className="border-t border-gray-200 p-2 bg-gray-50 rounded-b-lg">
                  <p className="text-xs text-gray-500 px-2">
                    Format matches CSV import template for round-trip compatibility
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {canCreate && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => navigate('/organization/venues/ai-research')}
            leftIcon={<AIIcon className="w-4 h-4" />}
          >
            AI Research
          </Button>
        )}
        {canRead && ungeolocatedCount !== undefined && ungeolocatedCount > 0 && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setFilters({ ...filters, ungeolocated: true });
              setPage(1);
            }}
            leftIcon={<MapPin className="w-4 h-4" />}
          >
            {ungeolocatedCount} Not Geolocated
          </Button>
        )}
        {canCreate && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setGooglePlacesModalOpen(true)}
            leftIcon={<MapPin className="w-4 h-4" />}
          >
            Import from Google
          </Button>
        )}
        {canCreate && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setImportModalOpen(true)}
            leftIcon={<Upload className="w-4 h-4" />}
          >
            Import CSV
          </Button>
        )}
      </div>

      {/* Filter Panel */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4 space-y-4">
        {/* Search Bar */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="flex-1">
            <Input
              placeholder="Search venues by name, city, or country..."
              value={filters.search || ''}
              onChange={(e) => updateFilter('search', e.target.value)}
              leftIcon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              }
            />
          </div>
          
          {/* Filter Toggle Button */}
          <Button
            variant={showAdvancedFilters || hasActiveFilters ? 'primary' : 'outline'}
            size="md"
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            leftIcon={<Filter className="w-4 h-4" />}
            rightIcon={showAdvancedFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          >
            Filters {hasActiveFilters && `(${Object.keys(filters).filter(k => k !== 'search' && k !== 'sort_by' && k !== 'sort_dir' && filters[k as keyof VenueFilters]).length})`}
          </Button>
        </div>

        {/* Advanced Filters - Collapsible */}
        {showAdvancedFilters && (
          <div className="pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {/* City Autocomplete */}
              <Autocomplete
                label="City"
                placeholder="Search city..."
                value={filters.city || ''}
                onChange={(value) => updateFilter('city', value)}
                onSearch={fetchCities}
                minChars={3}
                debounceMs={300}
              />

              {/* Country Autocomplete */}
              <Autocomplete
                label="Country"
                placeholder="Search country..."
                value={filters.country || ''}
                onChange={(value) => updateFilter('country', value)}
                onSearch={fetchCountries}
                minChars={3}
                debounceMs={300}
              />

              {/* Star Rating */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Star Rating
                </label>
                <select
                  value={filters.star_rating || ''}
                  onChange={(e) => updateFilter('star_rating', e.target.value ? parseInt(e.target.value) : undefined)}
                  className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                >
                  <option value="">All Ratings</option>
                  <option value="5">5 Stars</option>
                  <option value="4">4 Stars</option>
                  <option value="3">3 Stars</option>
                  <option value="2">2 Stars</option>
                  <option value="1">1 Star</option>
                </select>
              </div>

              {/* Min Rooms */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Min Rooms
                </label>
                <Input
                  type="number"
                  placeholder="e.g., 50"
                  value={filters.min_rooms || ''}
                  onChange={(e) => updateFilter('min_rooms', e.target.value ? parseInt(e.target.value) : undefined)}
                  min={0}
                />
              </div>

              {/* Min Capacity */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Min Capacity
                </label>
                <Input
                  type="number"
                  placeholder="e.g., 100"
                  value={filters.max_capacity || ''}
                  onChange={(e) => updateFilter('max_capacity', e.target.value ? parseInt(e.target.value) : undefined)}
                  min={0}
                />
              </div>

              {/* Min Meeting Spaces */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Min Meeting Spaces
                </label>
                <Input
                  type="number"
                  placeholder="e.g., 5"
                  value={filters.meeting_spaces_min || ''}
                  onChange={(e) => updateFilter('meeting_spaces_min', e.target.value ? parseInt(e.target.value) : undefined)}
                  min={0}
                />
              </div>

              {/* Max Airport Distance */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Distance to Airport (km)
                </label>
                <Input
                  type="number"
                  placeholder="e.g., 50"
                  value={filters.nearest_airport_distance_max || ''}
                  onChange={(e) => updateFilter('nearest_airport_distance_max', e.target.value ? parseFloat(e.target.value) : undefined)}
                  min={0}
                  step={0.1}
                />
              </div>

              {/* Research Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Research Status
                </label>
                <select
                  value={filters.enrichment_status || ''}
                  onChange={(e) => updateFilter('enrichment_status', e.target.value || undefined)}
                  className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                >
                  <option value="">All Statuses</option>
                  <option value="none">Not Started</option>
                  <option value="pending">In Progress</option>
                  <option value="enriched">Completed</option>
                  <option value="failed">Failed</option>
                </select>
              </div>

              {/* Research Freshness */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Research Freshness
                </label>
                <select
                  value={filters.enrichment_freshness_bucket || ''}
                  onChange={(e) => updateFilter('enrichment_freshness_bucket', e.target.value || undefined)}
                  className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                >
                  <option value="">Any Age</option>
                  <option value="30d+">Older than 30 days</option>
                  <option value="90d+">Older than 90 days</option>
                  <option value="6mo+">Older than 6 months</option>
                  <option value="1y+">Older than 1 year</option>
                </select>
              </div>

              {/* Spa Facilities */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Spa Facilities
                </label>
                <select
                  value={filters.has_spa === undefined ? '' : filters.has_spa ? 'true' : 'false'}
                  onChange={(e) => updateFilter('has_spa', e.target.value === '' ? undefined : e.target.value === 'true')}
                  className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                >
                  <option value="">Any</option>
                  <option value="true">With Spa</option>
                  <option value="false">No Spa</option>
                </select>
              </div>

              {/* Beach Access */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Beach Access
                </label>
                <select
                  value={filters.beach_access || ''}
                  onChange={(e) => updateFilter('beach_access', e.target.value || undefined)}
                  className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                >
                  <option value="">Any</option>
                  <option value="beachfront">Beachfront</option>
                  <option value="beach_access">Beach Access</option>
                  <option value="none">No Beach Access</option>
                </select>
              </div>

              {/* All-Inclusive */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  All-Inclusive
                </label>
                <select
                  value={filters.all_inclusive === undefined ? '' : filters.all_inclusive ? 'true' : 'false'}
                  onChange={(e) => updateFilter('all_inclusive', e.target.value === '' ? undefined : e.target.value === 'true')}
                  className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                >
                  <option value="">Any</option>
                  <option value="true">All-Inclusive Available</option>
                  <option value="false">Not All-Inclusive</option>
                </select>
              </div>

              {/* Property Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Property Type
                </label>
                <select
                  value={filters.property_type || ''}
                  onChange={(e) => updateFilter('property_type', e.target.value || undefined)}
                  className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                >
                  <option value="">Any</option>
                  <option value="adults_only">Adults Only</option>
                  <option value="family_friendly">Family Friendly</option>
                  <option value="mixed">Mixed</option>
                </select>
              </div>

              {/* Golf Course */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Golf Course
                </label>
                <select
                  value={filters.has_golf === undefined ? '' : filters.has_golf ? 'true' : 'false'}
                  onChange={(e) => updateFilter('has_golf', e.target.value === '' ? undefined : e.target.value === 'true')}
                  className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                >
                  <option value="">Any</option>
                  <option value="true">With Golf</option>
                  <option value="false">No Golf</option>
                </select>
              </div>

              {/* Min TripAdvisor Rating */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Min TripAdvisor Rating
                </label>
                <select
                  value={filters.min_tripadvisor_rating || ''}
                  onChange={(e) => updateFilter('min_tripadvisor_rating', e.target.value ? parseFloat(e.target.value) : undefined)}
                  className="flex h-10 w-full items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                >
                  <option value="">Any Rating</option>
                  <option value="4.5">4.5+</option>
                  <option value="4.0">4.0+</option>
                  <option value="3.5">3.5+</option>
                  <option value="3.0">3.0+</option>
                </select>
              </div>
            </div>

            {/* Clear Filters Button */}
            {hasActiveFilters && (
              <div className="mt-4 flex justify-end">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                >
                  Clear All Filters
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bulk Selection Bar */}
      {hasSelection && (
        <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 mb-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-center gap-3">
              <div className="text-sm font-medium text-primary-900">
                {selectAllFiltered ? (
                  <>
                    <span className="font-bold">{selectionCount}</span> venues selected (all matching filters)
                  </>
                ) : (
                  <>
                    <span className="font-bold">{selectionCount}</span> venue(s) selected on this page
                  </>
                )}
              </div>
              
              {!selectAllFiltered && selectedIds.size > 0 && (data?.pagination?.total || 0) > selectedIds.size && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSelectAllFiltered}
                  className="text-primary-700 hover:text-primary-800"
                >
                  Select all {data?.pagination?.total} matching filters
                </Button>
              )}
            </div>

            <div className="flex flex-wrap gap-2">
              {canUpdate && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleBulkAction('enrich')}
                  disabled={bulkActionMutation.isPending}
                  leftIcon={<AIIcon className="w-4 h-4" />}
                >
                  Run Research
                </Button>
              )}
              {canUpdate && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleBulkAction('refresh_airports')}
                  disabled={bulkActionMutation.isPending}
                >
                  Refresh Airports
                </Button>
              )}
              {canDelete && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleBulkAction('delete')}
                  disabled={bulkActionMutation.isPending}
                  className="text-error-600 hover:text-error-700 hover:border-error-300"
                >
                  Delete
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSelectedIds(new Set());
                  setSelectAllFiltered(false);
                }}
              >
                Clear Selection
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <Table
        data={data?.items || []}
        columns={displayColumns}
        loading={isLoading}
        serverSide={true}
        searchable={false}
        onSort={handleSort}
        onPaginate={(p, ps) => { setPage(p); setPageSize(ps); }}
        totalItems={data?.pagination?.total || 0}
        pagination={{
          pageSize: 50,
          showSizeChanger: true,
          pageSizeOptions: [25, 50, 100]
        }}
      />

      <VenueImportModal
        isOpen={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        onImportComplete={() => {
          queryClient.invalidateQueries({ queryKey: ['venues'] });
          setImportModalOpen(false);
        }}
      />

      <VenueFormModal
        isOpen={formModalOpen}
        venue={editingVenue}
        onClose={() => {
          setFormModalOpen(false);
          setEditingVenue(null);
        }}
        onSave={() => {
          queryClient.invalidateQueries({ queryKey: ['venues'] });
          setFormModalOpen(false);
          setEditingVenue(null);
        }}
      />

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        onConfirm={() => {
          if (confirmDialog.action) {
            confirmDialog.action();
          }
          setConfirmDialog({ ...confirmDialog, isOpen: false });
        }}
        title={confirmDialog.title}
        description={confirmDialog.description}
        variant={confirmDialog.variant}
        confirmText={confirmDialog.variant === 'danger' ? 'Delete' : 'Confirm'}
        isLoading={bulkActionMutation.isPending || deleteMutation.isPending}
      />

      {/* Venue Details Modal */}
      <VenueDetailsModal
        isOpen={detailsModalOpen}
        onClose={() => {
          setDetailsModalOpen(false);
          setSelectedVenue(null);
        }}
        venue={selectedVenue}
        isShortlisted={false}
        onAddToShortlist={undefined}
        isAddingToShortlist={false}
        onEditVenue={canUpdate ? (v) => {
          setDetailsModalOpen(false);
          setSelectedVenue(null);
          setEditingVenue(v as unknown as Venue);
          setFormModalOpen(true);
        } : undefined}
      />

      {/* Google Places Import Modal */}
      <GooglePlacesImportModal
        isOpen={googlePlacesModalOpen}
        onClose={() => setGooglePlacesModalOpen(false)}
        onImportComplete={() => queryClient.invalidateQueries({ queryKey: ['venues'] })}
      />
    </PageContainer>
  );
}
